import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { CATEGORY_TILE_CONFIGS } from "../../constants/logTypeRegistry";
import { getCalendarDateKey } from "../../utils/calendarDateKey";
import {
  buildTherapyPrepSourceReferences,
  sanitizeTherapyPrepSourceText,
} from "./reportBuilders/therapyPrepSourceReferences";
import {
  RANGE_PRESET,
  getCategoryColors,
  getCategoryMeta,
  getTherapyPrepCategory,
  getMeaningfulSummary,
  groupMedicationEntriesByDose,
  isMedicationEntry,
  toEntryDate,
} from "./reportBuilders/dailyCareReportEntryUtils";
import { formatSignalTierLabel } from "./reportBuilders/dailyCareReportBuilder";

const summaryLoadingPulse = keyframes`
  0% { opacity: 0.32; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-1px); }
  100% { opacity: 0.32; transform: translateY(0); }
`;

const getSectionEyebrowSx = (theme) => ({
  color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark,
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
});

const getSectionTitleSx = () => ({
  fontSize: { xs: "1.25rem", md: "1.38rem" },
  fontWeight: 700,
  lineHeight: 1.12,
});

const getSectionSubSx = () => ({
  color: "text.secondary",
  fontSize: "0.92rem",
  lineHeight: 1.65,
});

const getCategoryTone = (label = "", theme) => {
  const lower = String(label || "").toLowerCase();
  const semantic = theme.palette.semantic || {};
  const fallbackBg = semantic.sageMist || theme.palette.background.default;
  const fallbackInk = semantic.ink || theme.palette.text.primary;

  if (lower.includes("sleep")) return { bg: semantic.sleep?.bg || fallbackBg, ink: semantic.sleep?.ink || fallbackInk };
  if (lower.includes("food")) return { bg: semantic.categories?.food?.bg || fallbackBg, ink: semantic.categories?.food?.ink || fallbackInk };
  if (lower.includes("behavior")) return { bg: semantic.behavior?.bg || fallbackBg, ink: semantic.behavior?.ink || fallbackInk };
  if (lower.includes("med")) return { bg: semantic.meds?.bg || fallbackBg, ink: semantic.meds?.ink || fallbackInk };
  if (lower.includes("mood")) return { bg: alpha(fallbackBg, 0.7), ink: fallbackInk };

  return { bg: fallbackBg, ink: fallbackInk };
};

const CATEGORY_TILE_ICON_MAP = CATEGORY_TILE_CONFIGS.reduce((acc, item) => {
  acc[item.key] = item.icon || null;
  acc[item.category] = item.icon || null;
  return acc;
}, {});

const getSummaryCategoryIcon = (label = "") => {
  const lower = String(label || "").toLowerCase();
  if (lower.includes("sleep")) return CATEGORY_TILE_ICON_MAP.sleep || null;
  if (lower.includes("food")) return CATEGORY_TILE_ICON_MAP.food || null;
  if (lower.includes("behavior")) return CATEGORY_TILE_ICON_MAP.behavior || null;
  if (lower.includes("med")) return CATEGORY_TILE_ICON_MAP.meds || CATEGORY_TILE_ICON_MAP.medication || null;
  return null;
};

const getRangeSummaryTitle = (rangePreset) => {
  if (rangePreset === RANGE_PRESET.MONTH) return "Monthly Snapshot";
  if (rangePreset === RANGE_PRESET.CUSTOM) return "Snapshot";
  return "Weekly Snapshot";
};

const getRangeTitleLabel = (rangePreset, periodLabel = "") => {
  if (rangePreset === RANGE_PRESET.WEEK) return "Based on this week's notes";
  if (rangePreset === RANGE_PRESET.MONTH) return "Based on this month's notes";
  return periodLabel ? `Based on notes from ${periodLabel}` : "Based on notes from this selected range";
};

const normalizeConcern = (item) => {
  if (typeof item === "string") {
    return {
      label: "Concern",
      text: sanitizeTherapyPrepSourceText(item),
      signalTier: "medium_signal",
      entryIds: [],
    };
  }

  return {
    label: item?.label || "Concern",
    text: sanitizeTherapyPrepSourceText(item?.text || ""),
    signalTier: item?.signalTier || "medium_signal",
    entryIds: Array.isArray(item?.entryIds) ? item.entryIds : [],
  };
};

const getSurfaceSx = (theme) => ({
  bgcolor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.semantic?.line || alpha(theme.palette.text.primary, 0.1)}`,
  boxShadow: `0 12px 28px ${alpha(theme.palette.text.primary, 0.05)}`,
});

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const startOfCalendarDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getHeatColor = (count, maxCount, theme) => {
  const activityPalette = theme.palette.semantic.categories.activity;
  if (!count || !maxCount) return alpha(activityPalette.bg, 0.18);
  const intensity = count / maxCount;
  return alpha(activityPalette.bg, 0.28 + (intensity * 0.64));
};

const getHeatTextColor = (count, maxCount, theme) => (
  count && maxCount && count / maxCount >= 0.78
    ? theme.palette.common.white
    : theme.palette.text.primary
);

const buildActivityHeatMapModel = (entries = [], rangePreset, rangeStart = null, rangeEnd = null) => {
  const datedEntries = entries
    .map((entry) => ({ entry, date: toEntryDate(entry.timestamp || entry.date || entry.createdAt) }))
    .filter((item) => item.date instanceof Date && !Number.isNaN(item.date.getTime()));

  const today = startOfCalendarDay(new Date());
  const dates = datedEntries.map((item) => startOfCalendarDay(item.date));
  const fallbackStart = dates.length ? new Date(Math.min(...dates.map((date) => date.getTime()))) : today;
  const fallbackEnd = dates.length ? new Date(Math.max(...dates.map((date) => date.getTime()))) : today;
  const selectedStart = rangeStart instanceof Date && !Number.isNaN(rangeStart.getTime())
    ? startOfCalendarDay(rangeStart)
    : fallbackStart;
  const selectedEnd = rangeEnd instanceof Date && !Number.isNaN(rangeEnd.getTime())
    ? startOfCalendarDay(rangeEnd)
    : fallbackEnd;
  const monthAnchor = rangePreset === RANGE_PRESET.MONTH ? selectedStart : selectedEnd;
  const firstDay = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const lastDay = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);

  const dayMap = datedEntries.reduce((acc, { entry, date }) => {
    const key = getCalendarDateKey(date);
    if (!key) return acc;
    if (!acc[key]) {
      acc[key] = {
        count: 0,
        behaviorCount: 0,
        categories: {},
      };
    }

    const category = getTherapyPrepCategory(entry) || "dailyLog";
    acc[key].count += 1;
    acc[key].categories[category] = (acc[key].categories[category] || 0) + 1;
    if (category === "behavior") {
      acc[key].behaviorCount += 1;
    }
    return acc;
  }, {});

  const categoryCounts = datedEntries.reduce((acc, { entry }) => {
    const category = getTherapyPrepCategory(entry) || "dailyLog";
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  const startOffset = firstDay.getDay();
  const calendarDays = [
    ...Array.from({ length: startOffset }, (_, index) => ({ id: `blank-${index}`, blank: true })),
  ];

  for (let date = firstDay; date <= lastDay; date = addDays(date, 1)) {
    const key = getCalendarDateKey(date);
    calendarDays.push({
      id: key,
      key,
      day: date.getDate(),
      label: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      isToday: getCalendarDateKey(date) === getCalendarDateKey(today),
      inSelectedRange: date >= selectedStart && date <= selectedEnd,
      ...(dayMap[key] || { count: 0, behaviorCount: 0, categories: {} }),
    });
  }

  const title = monthAnchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return {
    title,
    totalEntries: datedEntries.length,
    activeDays: Object.values(dayMap).filter((day) => day.count > 0).length,
    maxDayCount: Math.max(0, ...Object.values(dayMap).map((day) => day.count || 0)),
    calendarDays,
    categoryCounts,
  };
};

const SectionHeading = ({ index, eyebrow, title, subtitle, theme }) => (
  <Box sx={{ mb: 2.1, pt: 0.15 }}>
    <Box sx={{ display: "grid", gridTemplateColumns: index ? "32px 1px minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 1.35, alignItems: "start" }}>
      {index ? (
        <>
          <Typography sx={{ color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark, fontSize: "0.9rem", fontWeight: 700, lineHeight: 1 }}>
            {index}
          </Typography>
          <Box sx={{ width: 1, minHeight: 42, bgcolor: theme.palette.semantic?.line || alpha(theme.palette.text.primary, 0.12) }} />
        </>
      ) : null}
      <Stack spacing={0.28}>
        <Typography sx={getSectionEyebrowSx(theme)}>
          {eyebrow || title}
        </Typography>
        <Typography sx={getSectionTitleSx()}>{title}</Typography>
        {subtitle ? <Typography sx={getSectionSubSx()}>{subtitle}</Typography> : null}
      </Stack>
    </Box>
  </Box>
);

const TherapyPrepSourceReferences = ({
  sourceIds = [],
  sourceEntries = [],
  onOpenTimelineSource,
  theme,
}) => {
  const references = buildTherapyPrepSourceReferences(sourceIds, sourceEntries);
  if (!references.length) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.65} sx={{ mt: 0.8, flexWrap: "wrap", rowGap: 0.65 }}>
      {references.map((reference) => {
        const canOpen = reference.linked && typeof onOpenTimelineSource === "function";
        const handleOpen = (event) => {
          if (!canOpen) {
            return;
          }
          event.stopPropagation();
          onOpenTimelineSource(reference.entry);
        };

        return (
          <Box
            key={reference.id}
            component="span"
            role={canOpen ? "link" : undefined}
            tabIndex={canOpen ? 0 : undefined}
            onClick={handleOpen}
            onKeyDown={canOpen ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleOpen(event);
              }
            } : undefined}
            sx={{
              maxWidth: "100%",
              display: "inline-flex",
              alignItems: "center",
              minHeight: 24,
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              bgcolor: canOpen
                ? alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.35)
                : alpha(theme.palette.text.primary, 0.06),
              color: canOpen
                ? theme.palette.semantic?.sageDeep || theme.palette.primary.dark
                : "text.secondary",
              cursor: canOpen ? "pointer" : "default",
              fontSize: "0.8125rem",
              fontWeight: 700,
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": canOpen ? {
                bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.55),
              } : undefined,
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.semantic?.sageDeep || theme.palette.primary.dark}`,
                outlineOffset: 2,
              },
            }}
          >
            {reference.label}
          </Box>
        );
      })}
    </Stack>
  );
};

export const DailyCareReportDateRangeControls = ({ rangePreset, setRangePreset }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.1,
      flexWrap: "nowrap",
      overflowX: "auto",
      pb: 0.25,
    }}
  >
    {[
      { key: RANGE_PRESET.WEEK, label: "This Week" },
      { key: RANGE_PRESET.MONTH, label: "This Month" },
      { key: RANGE_PRESET.CUSTOM, label: "Custom Range" },
    ].map((item) => {
      const active = rangePreset === item.key;
      return (
        <Chip
          key={item.key}
          label={item.label}
          clickable
          onClick={() => setRangePreset(item.key)}
          sx={(theme) => ({
            borderRadius: "999px",
            px: 0.5,
            height: 42,
            border: `1px solid ${active ? theme.palette.semantic?.sage || theme.palette.primary.main : alpha(theme.palette.text.primary, 0.14)}`,
            bgcolor: active ? theme.palette.semantic?.sage || theme.palette.primary.main : alpha(theme.palette.background.paper, 0.82),
            color: active ? theme.palette.background.paper : theme.palette.text.primary,
            fontWeight: 600,
            fontSize: "0.95rem",
            boxShadow: active ? `0 4px 12px ${alpha(theme.palette.semantic?.sageDeep || theme.palette.primary.main, 0.22)}` : "none",
            "& .MuiChip-label": { px: 2, py: 0.25 },
          })}
        />
      );
    })}
  </Box>
);

export const DailyCareReportSummarySection = ({ summaryData, summaryLoading, periodLabel, rangePreset, theme }) => {
  const bullets = Array.isArray(summaryData?.bullets) ? summaryData.bullets : [];
  const leadLetter = (summaryData?.weeklySummary || "T").trim().charAt(0) || "T";

  return (
    <section className="therapy-print-avoid">
      <SectionHeading
        index="01"
        eyebrow="Weekly snapshot"
        title={getRangeSummaryTitle(rangePreset)}
        subtitle="High-value highlights for the next visit at a glance."
        theme={theme}
      />

      <Stack spacing={2}>
        {summaryLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 0.45 }}>
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: alpha(theme.palette.primary.main, 0.7),
                    animation: `${summaryLoadingPulse} 1.1s ease-in-out infinite`,
                    animationDelay: `${index * 0.18}s`,
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
              Preparing report summary...
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            px: { xs: 1.45, sm: 1.7 },
            py: 1.45,
            borderRadius: "16px",
            borderLeft: `3px solid ${theme.palette.semantic?.sage || theme.palette.primary.main}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.56)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.96rem",
              fontWeight: 750,
              lineHeight: 1.72,
              color: "text.primary",
            }}
          >
            <Box component="span" sx={{ color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark, fontWeight: 700 }}>
              {leadLetter}
            </Box>
            <Box component="span" sx={{ pl: 0.2 }}>
              {String(summaryData?.weeklySummary || "").slice(1)}
            </Box>
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.6,
            py: 0.85,
            borderRadius: "14px",
            bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.28),
            border: `1px solid ${alpha(theme.palette.semantic?.sage || theme.palette.primary.main, 0.14)}`,
            display: "inline-flex",
            alignSelf: "flex-start",
          }}
        >
          <Typography sx={{ ...getSectionEyebrowSx(theme), mb: 0 }}>
            {getRangeTitleLabel(rangePreset, periodLabel)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.35,
          }}
        >
          {bullets.map((bullet, index) => {
            const tone = getCategoryTone(bullet?.label, theme);
            const isSparse = /not enough|limited|not added|brief|mostly routine/i.test(bullet?.text || "");
            const IconComponent = getSummaryCategoryIcon(bullet?.label);
            return (
              <Paper
                key={bullet.label}
                elevation={0}
                className="therapy-print-avoid"
                sx={{
                  p: 1.65,
                  borderRadius: "16px",
                  borderTop: `3px solid ${tone.ink || theme.palette.primary.main}`,
                  ...getSurfaceSx(theme),
                  bgcolor: isSparse ? alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.22) : theme.palette.background.paper,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.1 }}>
                  {IconComponent ? (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "8px",
                        bgcolor: alpha(tone.bg, 0.9),
                        color: tone.ink,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent sx={{ fontSize: 14, color: tone.ink }} />
                    </Box>
                  ) : (
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: tone.ink }} />
                  )}
                  <Typography sx={{ ...getSectionEyebrowSx(theme), color: alpha(tone.ink, 0.92) }}>
                    {bullet.label}
                  </Typography>
                  <Chip
                    label={`${index + 1}`}
                    size="small"
                    sx={{
                      ml: "auto",
                      height: 24,
                      borderRadius: "999px",
                      bgcolor: alpha(tone.bg || theme.palette.primary.main, 0.42),
                      color: tone.ink || theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Typography sx={{ color: isSparse ? "text.secondary" : "text.primary", lineHeight: 1.65, fontStyle: isSparse ? "italic" : "normal" }}>
                  {bullet.text}
                </Typography>
                {isSparse ? (
                  <Box
                    sx={{
                      mt: 1.6,
                      pt: 1.3,
                      borderTop: `1px dashed ${alpha(theme.palette.semantic?.sage || theme.palette.primary.main, 0.4)}`,
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.2,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.semantic?.sage || theme.palette.primary.main, 0.16),
                        color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark,
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        lineHeight: 1,
                      }}
                    >
                      +
                    </Box>
                    <Typography sx={{ color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark, fontSize: "0.88rem", lineHeight: 1.5 }}>
                      Add a few more {String(bullet.label || "").toLowerCase()} notes before Friday’s session to make this category more useful.
                    </Typography>
                  </Box>
                ) : null}
              </Paper>
            );
          })}
        </Box>
      </Stack>
    </section>
  );
};

export const DailyCareReportActivityHeatMapSection = ({
  entries,
  rangePreset,
  rangeStart,
  rangeEnd,
  theme,
}) => {
  const model = useMemo(
    () => buildActivityHeatMapModel(entries, rangePreset, rangeStart, rangeEnd),
    [entries, rangeEnd, rangePreset, rangeStart]
  );
  const rhythmLabel = model.activeDays
    ? `You've kept a steady rhythm — logged on ${model.activeDays} of ${model.calendarDays.filter((day) => !day.blank && day.inSelectedRange).length || 1} days.`
    : "Start logging and Lifelog will show the rhythm here.";

  return (
    <section className="therapy-print-avoid">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.4 },
          borderRadius: "22px",
          ...getSurfaceSx(theme),
        }}
      >
        <Box sx={{ mb: 2.15 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.55, mb: 1.1 }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "999px",
                bgcolor: theme.palette.semantic?.sageDeep || theme.palette.primary.dark,
              }}
            />
            <Typography sx={getSectionEyebrowSx(theme)}>
              Foundation
            </Typography>
          </Box>
          <Typography sx={{ color: "text.primary", fontWeight: 850, fontSize: { xs: "1.25rem", sm: "1.38rem" }, lineHeight: 1.14 }}>
            Logging rhythm
          </Typography>
          <Typography sx={{ mt: 0.65, color: "text.secondary", fontSize: "0.95rem", fontWeight: 650, lineHeight: 1.55 }}>
            {rhythmLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gap: { xs: 0.55, sm: 0.75 },
          }}
        >
          {DAY_LABELS.map((label, index) => (
            <Typography
              key={`${label}-${index}`}
              sx={{
                color: alpha(theme.palette.text.primary, 0.5),
                fontSize: "0.74rem",
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              {label}
            </Typography>
          ))}

          {model.calendarDays.map((day) => {
            if (day.blank) {
              return <Box key={day.id} aria-hidden="true" />;
            }

            const isMuted = !day.inSelectedRange && !day.count && !day.isToday;
            const activityPalette = theme.palette.semantic.categories.activity;
            return (
              <Box
                key={day.id}
                aria-label={`${day.label}: ${day.count} ${day.count === 1 ? "log" : "logs"}`}
                title={`${day.label}: ${day.count} ${day.count === 1 ? "log" : "logs"}`}
                sx={{
                  aspectRatio: "1 / 1",
                  minHeight: { xs: 42, sm: 48 },
                  borderRadius: "8px",
                  bgcolor: day.isToday
                    ? getHeatColor(Math.max(day.count, 1), model.maxDayCount || 1, theme)
                    : isMuted
                      ? "transparent"
                      : getHeatColor(day.count, model.maxDayCount, theme),
                  color: day.isToday
                    ? getHeatTextColor(Math.max(day.count, 1), model.maxDayCount || 1, theme)
                    : isMuted
                      ? alpha(theme.palette.text.primary, 0.38)
                      : getHeatTextColor(day.count, model.maxDayCount, theme),
                  border: `1px solid ${day.isToday || !isMuted ? alpha(activityPalette.ink, 0.16) : "transparent"}`,
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.65, mt: 1.45 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: 750 }}>
            Less
          </Typography>
          {[0, 1, 2, 3].map((level) => (
            <Box
              key={level}
              sx={{
                width: 16,
                height: 16,
                borderRadius: "5px",
                bgcolor: getHeatColor(level, 3, theme),
              }}
            />
          ))}
          <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: 750 }}>
            More
          </Typography>
        </Box>
      </Paper>
    </section>
  );
};

export const DailyCareReportTopConcernsSection = ({
  concerns,
  sourceEntries = [],
  onOpenTimelineSource,
  theme,
}) => {
  const items = Array.isArray(concerns) ? concerns.map(normalizeConcern) : [];
  return (
    <section className="therapy-print-avoid">
      <SectionHeading
        index="02"
        eyebrow="Top concerns"
        title="Top Concerns"
        subtitle="The highest-signal items surfaced from the period."
        theme={theme}
      />

      <Stack spacing={1.8}>
        {items.length ? items.map((item, index) => {
          const high = item.signalTier === "high_signal";
          return (
            <Paper
              key={`${item.label}-${index}`}
              elevation={0}
              sx={{
                p: 1.8,
                borderRadius: "16px",
                borderLeft: `4px solid ${high ? theme.palette.semantic?.behavior?.ink || theme.palette.error.main : theme.palette.warning?.main || theme.palette.primary.main}`,
                ...getSurfaceSx(theme),
              }}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" }, gap: 1.6, alignItems: "start" }}>
                <Chip
                  label={high ? "High concern" : "Moderate concern"}
                  sx={{
                    justifySelf: "start",
                    borderRadius: "999px",
                    bgcolor: high ? alpha(theme.palette.semantic?.behavior?.bg || theme.palette.error.main, 0.72) : alpha(theme.palette.warning?.light || theme.palette.primary.light, 0.34),
                    color: high ? theme.palette.semantic?.behavior?.ink || theme.palette.error.dark : theme.palette.warning?.dark || theme.palette.primary.dark,
                    fontWeight: 700,
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.55 }}>
                    {item.text}
                  </Typography>
                  <TherapyPrepSourceReferences
                    sourceIds={item.entryIds}
                    sourceEntries={sourceEntries}
                    onOpenTimelineSource={onOpenTimelineSource}
                    theme={theme}
                  />
                  <Box
                    sx={{
                      mt: 1.3,
                      p: 1.5,
                      borderLeft: `2px solid ${high ? theme.palette.semantic?.behavior?.ink || theme.palette.error.main : theme.palette.warning?.main || theme.palette.primary.main}`,
                      borderRadius: "0 10px 10px 0",
                      bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.18),
                    }}
                  >
                    <Typography sx={{ color: "text.secondary", lineHeight: 1.65 }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        }) : (
          <Typography sx={{ color: "text.secondary" }}>No high-signal concerns stood out in this period.</Typography>
        )}
      </Stack>
    </section>
  );
};

export const DailyCareReportPatternsSection = ({ patterns, theme }) => {
  const items = Array.isArray(patterns) ? patterns.filter(Boolean) : [];
  const hasClearPatterns = items.length && !items.every((item) => /nothing clear stood out/i.test(item));

  return (
    <section className="therapy-print-avoid">
      <SectionHeading
        index="03"
        eyebrow="Patterns"
        title="What seemed to line up"
        subtitle="Patterns only belong here when the entries directly support them."
        theme={theme}
      />

      {hasClearPatterns ? (
        <Stack component="ul" spacing={1.25} sx={{ mt: 0, pl: 2.5, color: "text.primary" }}>
          {items.map((item) => (
            <Typography component="li" key={item} sx={{ lineHeight: 1.65 }}>
              {item}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            border: `1px dashed ${alpha(theme.palette.semantic?.sage || theme.palette.primary.main, 0.42)}`,
            bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.18),
          }}
        >
          <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>
            Nothing clear stood out strongly enough to call it a reliable pattern. A few more sleep, food, or transition notes before the next session would make this section far more useful.
          </Typography>
        </Box>
      )}
    </section>
  );
};

export const DailyCareReportQuestionsSection = ({
  questions,
  sourceEntries = [],
  onOpenTimelineSource,
  theme,
}) => {
  const [checkedQuestions, setCheckedQuestions] = useState(() => new Set());

  const normalizedQuestions = Array.isArray(questions)
    ? questions
        .map((item) => {
          if (typeof item === "string") {
            const question = sanitizeTherapyPrepSourceText(item);
            return question ? { question, why: "", sourceEntryIds: [] } : null;
          }

          const question = sanitizeTherapyPrepSourceText(item?.question || "");
          if (!question) {
            return null;
          }

          return {
            question,
            why: sanitizeTherapyPrepSourceText(item?.why || ""),
            sourceEntryIds: Array.isArray(item?.sourceEntryIds) ? item.sourceEntryIds : [],
          };
        })
        .filter(Boolean)
    : [];

  const toggleQuestion = (question) => {
    setCheckedQuestions((current) => {
      const next = new Set(current);
      if (next.has(question)) {
        next.delete(question);
      } else {
        next.add(question);
      }
      return next;
    });
  };

  return (
    <section className="therapy-print-avoid">
      <SectionHeading
        index="04"
        eyebrow="Questions"
        title="Questions to discuss"
        subtitle="Prompts that make the session easier to use in real time."
        theme={theme}
      />

      <Stack spacing={0.75}>
        {normalizedQuestions.map((item, index) => {
          const checked = checkedQuestions.has(item.question);
          return (
            <ButtonBase
              key={item.question}
              onClick={() => toggleQuestion(item.question)}
              sx={{
                width: "100%",
                justifyContent: "stretch",
                textAlign: "left",
                borderRadius: "14px",
                bgcolor: checked ? alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.42) : "transparent",
                "&:hover": {
                  bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, checked ? 0.5 : 0.22),
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  py: 1.15,
                  px: 1,
                  display: "grid",
                  gridTemplateColumns: "28px 24px minmax(0, 1fr)",
                  gap: 1.05,
                  alignItems: "center",
                  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "7px",
                    border: `1px solid ${checked ? theme.palette.semantic?.sageDeep || theme.palette.primary.dark : alpha(theme.palette.text.primary, 0.22)}`,
                    bgcolor: checked ? theme.palette.semantic?.sageDeep || theme.palette.primary.dark : theme.palette.background.paper,
                    color: theme.palette.background.paper,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {checked ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : null}
                </Box>
                <Typography sx={{ color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark, fontWeight: 700, fontSize: "0.86rem" }}>
                  {index + 1}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.96rem",
                    fontWeight: 750,
                    lineHeight: 1.42,
                    textDecoration: checked ? "line-through" : "none",
                    color: checked ? alpha(theme.palette.text.primary, 0.58) : theme.palette.text.primary,
                  }}
                >
                  {item.question}
                </Typography>
                {item.why ? (
                  <Box sx={{ gridColumn: "3 / 4", mt: -0.35 }}>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.82rem",
                        lineHeight: 1.45,
                      }}
                    >
                      {item.why}
                    </Typography>
                    <TherapyPrepSourceReferences
                      sourceIds={item.sourceEntryIds}
                      sourceEntries={sourceEntries}
                      onOpenTimelineSource={onOpenTimelineSource}
                      theme={theme}
                    />
                  </Box>
                ) : item.sourceEntryIds.length ? (
                  <Box sx={{ gridColumn: "3 / 4", mt: -0.35 }}>
                    <TherapyPrepSourceReferences
                      sourceIds={item.sourceEntryIds}
                      sourceEntries={sourceEntries}
                      onOpenTimelineSource={onOpenTimelineSource}
                      theme={theme}
                    />
                  </Box>
                ) : null}
              </Box>
            </ButtonBase>
          );
        })}
      </Stack>

      <ButtonBase
        sx={{
          mt: 1.25,
          px: 1.2,
          py: 0.8,
          borderRadius: "999px",
          color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark,
          fontWeight: 600,
          "&:hover": {
            bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.28),
          },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18, mr: 0.45 }} />
        Add your own question
      </ButtonBase>
    </section>
  );
};

export const DailyCareReportKeyEventsSection = ({
  keyEvents,
  sourceEntries = [],
  onOpenTimelineSource,
  theme,
}) => (
  <section className="therapy-print-avoid">
    <SectionHeading
      index="05"
      eyebrow="Key events"
      title="Key events"
      subtitle="The shortest path through the period before dropping into full logs."
      theme={theme}
    />

    <Box
      sx={{
        position: "relative",
        pl: { xs: 2.35, sm: 3 },
        "&::before": {
          content: '""',
          position: "absolute",
          left: { xs: 8, sm: 10 },
          top: 8,
          bottom: 8,
          width: "1px",
          bgcolor: theme.palette.semantic?.line || alpha(theme.palette.text.primary, 0.1),
        },
      }}
    >
      {keyEvents.length ? keyEvents.map((event) => (
        <Box
          key={event.id}
          sx={{
            position: "relative",
            py: 0.65,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: { xs: -26, sm: -32 },
              top: 24,
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: (getCategoryColors({ category: event.category, type: event.category }) || {}).text || theme.palette.semantic?.sageDeep || theme.palette.primary.dark,
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: `0 0 0 4px ${alpha((getCategoryColors({ category: event.category, type: event.category }) || {}).bg || theme.palette.primary.main, 0.5)}`,
            }}
          />
          <Box
            sx={{
              p: 1.45,
              borderRadius: "16px",
              ...getSurfaceSx(theme),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.2, mb: 0.9, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                {toEntryDate(event.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · {toEntryDate(event.date).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.65 }}>
                <Chip
                  label={event.label}
                  size="small"
                  sx={{
                    borderRadius: "999px",
                    bgcolor: (getCategoryColors({ category: event.category, type: event.category }) || {}).bg || alpha(theme.palette.text.primary, 0.06),
                    color: (getCategoryColors({ category: event.category, type: event.category }) || {}).text || theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={formatSignalTierLabel(event.signalTier)}
                  size="small"
                  sx={{
                    borderRadius: "999px",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
            <Typography sx={{ color: "text.primary", lineHeight: 1.62, fontSize: "0.93rem" }}>
              {event.text}
            </Typography>
            {Array.isArray(event.entryIds) && event.entryIds.length ? (
              <TherapyPrepSourceReferences
                sourceIds={event.entryIds}
                sourceEntries={sourceEntries}
                onOpenTimelineSource={onOpenTimelineSource}
                theme={theme}
              />
            ) : null}
          </Box>
        </Box>
      )) : (
        <Typography sx={{ color: "text.secondary" }}>No standout events surfaced for this period.</Typography>
      )}
    </Box>
  </section>
);

export const DailyCareReportDateSections = ({ dateSections, theme }) => (
  <Stack spacing={3}>
    {dateSections.map((section) => (
      <Box key={section.date.toISOString()} className="therapy-print-avoid">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            alignItems: "baseline",
            pb: 1.1,
            borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.09)}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "1.02rem",
              fontWeight: 700,
            }}
          >
            {section.date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </Typography>
          <Typography sx={{ ...getSectionEyebrowSx(theme), color: "text.secondary" }}>
            {section.items.length} {section.items.length === 1 ? "entry" : "entries"}
          </Typography>
        </Box>

        <Stack spacing={0}>
          {groupMedicationEntriesByDose(section.items.filter(isMedicationEntry)).map((group) => (
            <Box
              key={`${group.name}-${group.dose || "no-dose"}`}
              sx={{
                py: 1.4,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "90px minmax(0, 1fr) 60px" },
                gap: 1.5,
                borderBottom: `1px dotted ${alpha(theme.palette.text.primary, 0.18)}`,
              }}
            >
              <Typography sx={{ ...getSectionEyebrowSx(theme), color: theme.palette.semantic?.meds?.ink || theme.palette.primary.dark }}>
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.7 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: theme.palette.semantic?.meds?.ink || theme.palette.primary.dark }} />
                  Medication
                </Box>
              </Typography>
              <Typography sx={{ color: "text.primary", lineHeight: 1.6 }}>
                {group.dose ? `${group.name} ${group.dose}` : group.name} — {group.givenCount && group.missedCount
                  ? `given ${group.givenCount} times, missed ${group.missedCount} times`
                  : `given ${group.givenCount || group.missedCount} times`}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums", textAlign: { xs: "left", md: "right" } }}>
                —
              </Typography>
            </Box>
          ))}

          {section.items.filter((entry) => !isMedicationEntry(entry)).map((entry) => {
            const meta = getCategoryMeta(entry);
            const palette = getCategoryColors(entry) || {};
            return (
              <Box
                key={entry.id}
                sx={{
                  py: 1.35,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "90px minmax(0, 1fr) 60px" },
                  gap: 1.5,
                  alignItems: "start",
                  borderBottom: `1px dotted ${alpha(theme.palette.text.primary, 0.18)}`,
                }}
              >
                <Typography
                  sx={{
                    ...getSectionEyebrowSx(theme),
                    color: palette.text || theme.palette.text.primary,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.8,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: palette.text || theme.palette.text.primary, flexShrink: 0 }} />
                  {meta.label}
                </Typography>
                <Typography sx={{ color: "text.primary", lineHeight: 1.62 }}>
                  {getMeaningfulSummary(entry)}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums", textAlign: { xs: "left", md: "right" } }}>
                  {toEntryDate(entry.timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
    ))}
  </Stack>
);

export const DailyCareReportEmptyState = ({ childName, periodLabel, onLogSomething, theme }) => (
  <Paper
    elevation={0}
    sx={{
      p: 4,
      borderRadius: "20px",
      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      bgcolor: alpha(theme.palette.semantic?.sageMist || theme.palette.primary.main, 0.28),
    }}
  >
    <Typography sx={{ ...getSectionEyebrowSx(theme), mb: 1 }}>
      Visit Prep
    </Typography>
    <Typography sx={{ fontSize: "1.72rem", fontWeight: 700, lineHeight: 1.12 }}>
      A report needs more notes first.
    </Typography>
    <Typography sx={{ mt: 1.2, color: "text.secondary", lineHeight: 1.7, maxWidth: 580 }}>
      There are no entries for {childName} in {periodLabel} yet. Log a few meaningful moments and this page will turn them into something useful for the care team to skim or print.
    </Typography>
    <Link
      component="button"
      type="button"
      onClick={onLogSomething}
      sx={{ mt: 2, fontWeight: 600, color: theme.palette.semantic?.sageDeep || theme.palette.primary.dark }}
    >
      Log something
    </Link>
  </Paper>
);
